import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import Badge from '@/shared/ui/components/basic/Badge';
import type { Vistory, VistoryRoom } from '../types/vistory.types';
import { VistoryPhotoUploader } from './VistoryPhotoUploader';
import { VistoryPhotoGallery } from './VistoryPhotoGallery';

interface Props {
  room: VistoryRoom;
  vistory: Vistory;
  agencyId: string;
  onUpdate: () => void;
}

/**
 * Single room card with name, photo count badge, per-room uploader, and
 * per-room thumbnail gallery.
 */
export function VistoryRoomCard({ room, vistory, agencyId, onUpdate }: Props) {
  const { t } = useTranslation('features/vistories');
  // Rendered only from within VistoryRoomEditor, which the parent panel
  // already gates on isEditable (draft or rejected) — mirror that here
  // so a rejected vistory's rooms stay editable too, not just draft's.
  const isEditable = vistory.status === 'draft' || vistory.status === 'rejected';
  const photoCount = room.photos?.length ?? 0;

  return (
    <div className="rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-3)] shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between mb-[var(--space-2)]">
        <Text size="sm" weight="medium" className="text-[var(--text-primary)]">
          {room.name}
        </Text>
        <Badge color="var(--text-secondary)" backgroundColor="var(--surface-tertiary)" size="sm">
          {photoCount} {t('vistory.photos_count')}
        </Badge>
      </div>

      <VistoryPhotoGallery vistory={vistory} agencyId={agencyId} photos={room.photos} />

      {isEditable && (
        <div className="mt-[var(--space-2)]">
          <VistoryPhotoUploader
            agencyId={agencyId}
            vistoryId={vistory.id}
            roomId={room.id}
            onUploaded={onUpdate}
          />
        </div>
      )}
    </div>
  );
}
